import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { canAccessModule } from '@/config/permissions';
import { logPermissionDeniedDebounced } from '@/services/audit';
import { notifyPermissionDeniedDebounced } from '@/services/events/permissionDeniedNotify';
import { routePaths } from '@/routes/paths';
import { useAuth } from '@/shared/hooks/useAuth';
import type { ModuleId } from '@/types';

type RequireModuleAccessProps = {
  moduleId: ModuleId;
  children: ReactNode;
};

export function RequireModuleAccess({
  moduleId,
  children,
}: RequireModuleAccessProps) {
  const { profile, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <p className="auth-muted">Cargando…</p>;
  }

  if (!profile?.active || profile.role === 'pending') {
    return <Navigate to={routePaths.accessPending} replace />;
  }

  if (!canAccessModule(profile.role, moduleId)) {
    logPermissionDeniedDebounced(`module:${moduleId}`, {
      metadata: {
        moduleId,
        path: location.pathname,
        role: profile.role,
      },
    });
    notifyPermissionDeniedDebounced(`module:${moduleId}`, {
      permission: `module:${moduleId}`,
      path: location.pathname,
    });
    return <Navigate to={routePaths.accessDenied} replace />;
  }

  return <>{children}</>;
}
